import { Link } from "react-router-dom";
import { ArrowRight, CheckCircle2, Microscope, Droplets, Waves, ClipboardList, Users, Headset } from "lucide-react";
import SectionHeading from "@/components/SectionHeading";
import ImageWithFallback from "@/components/ImageWithFallback";
import { IMAGES } from "@/lib/brand";
import { useSeo } from "@/lib/useSeo";

const FACTORS = [
  "Pond condition", "Species", "Culture stage", "Water quality", "Mineral requirements",
  "Stress conditions", "Pond-bottom condition", "Feed performance", "Overall culture requirements",
];

const SERVICES = [
  { title: "Application Guidance", desc: "Practical, pond-specific guidance on how and when to apply BIOSYN solutions.", icon: ClipboardList },
  { title: "Water & Pond Advisory", desc: "Support based on water quality and pond-bottom condition.", icon: Waves },
  { title: "Culture-Stage Support", desc: "Recommendations tailored to species and culture stage.", icon: Droplets },
  { title: "On-Field Technical Support", desc: "Support for farmers, dealers and aquaculture professionals.", icon: Users },
];

export default function TechnicalServices() {
  useSeo({
    title: "Technical Services | BIOSYN Aquaculture",
    description: "BIOSYN combines product technology with practical technical support based on pond condition, species, culture stage, water quality and more.",
    image: IMAGES.lab,
  });

  return (
    <div>
      <section className="relative overflow-hidden" style={{ background: "#063B63" }}>
        <ImageWithFallback src={IMAGES.microscope} alt="Aquaculture laboratory technical services" className="absolute inset-0 w-full h-full object-cover opacity-25" />
        <div className="container-x relative py-20 md:py-28">
          <p className="overline-label" style={{ color: "#16B8D4" }}>Technical Services</p>
          <h1 className="mt-3 font-heading text-4xl md:text-5xl lg:text-6xl font-extrabold text-white max-w-4xl leading-tight">
            Technical Support for Better Pond Management
          </h1>
        </div>
      </section>

      <section className="section container-x">
        <div className="grid lg:grid-cols-2 gap-12 items-center">
          <div>
            <SectionHeading overline="How We Support You" title="Product Technology + Practical Guidance" />
            <p className="mt-5 text-base leading-relaxed" style={{ color: "#4A6B82" }}>
              BIOSYN combines product technology with practical application guidance. Our recommendations are based on the
              real conditions of your pond and culture:
            </p>
            <div className="mt-6 grid sm:grid-cols-2 gap-2.5">
              {FACTORS.map((f) => (
                <div key={f} className="flex items-center gap-2.5 text-sm" style={{ color: "#17324D" }}>
                  <CheckCircle2 className="w-4 h-4 shrink-0" style={{ color: "#3FAE35" }} />{f}
                </div>
              ))}
            </div>
            <Link to="/contact" className="btn-primary mt-8" data-testid="tech-talk-team">Talk to Our Technical Team <ArrowRight className="w-4 h-4" /></Link>
          </div>
          <div className="rounded-3xl overflow-hidden biosyn-card !p-0">
            <ImageWithFallback src={IMAGES.lab} alt="Specialist analysing water microbiology" className="w-full h-[420px] object-cover" />
          </div>
        </div>
      </section>

      <section className="section grain" style={{ background: "#FFFFFF" }}>
        <div className="container-x">
          <SectionHeading align="center" overline="Our Services" title="Practical, Pond-Focused Support" />
          <div className="mt-12 grid sm:grid-cols-2 lg:grid-cols-4 gap-6">
            {SERVICES.map((s) => (
              <div key={s.title} className="biosyn-card p-7">
                <div className="w-12 h-12 rounded-xl flex items-center justify-center mb-4" style={{ background: "rgba(8,127,191,0.09)" }}>
                  <s.icon className="w-6 h-6" style={{ color: "#087FBF" }} />
                </div>
                <h3 className="font-heading font-bold text-lg" style={{ color: "#063B63" }}>{s.title}</h3>
                <p className="mt-2 text-sm leading-relaxed" style={{ color: "#4A6B82" }}>{s.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="section" style={{ background: "#F5FAFC" }}>
        <div className="container-x text-center">
          <Headset className="w-10 h-10 mx-auto mb-4" style={{ color: "#3FAE35" }} />
          <h2 className="font-heading text-2xl md:text-3xl font-extrabold" style={{ color: "#063B63" }}>Need guidance for your pond?</h2>
          <p className="mt-3 max-w-xl mx-auto text-sm" style={{ color: "#4A6B82" }}>Our technical team is ready to help with practical, application-oriented advice.</p>
          <div className="mt-6"><Link to="/contact" className="btn-primary" data-testid="tech-contact-cta">Talk to a Technical Expert <ArrowRight className="w-4 h-4" /></Link></div>
        </div>
      </section>
    </div>
  );
}
